import { supabase } from '../../infrastructure/supabase/supabaseClient';

export interface Estado {
  estado_id: number;
  nombre_estado: string;
}

export interface Ciudad {
  ciudad_id: number;
  ciudad_nombre: string;
  latitud: number;
  longitud: number;
  estado_nombre?: string; // Utilizado en la búsqueda predictiva
}

export class GeografiaService {
  /**
   * Obtiene la lista de todos los estados precargados en el sistema.
   */
  static async getEstados(): Promise<Estado[]> {
    const { data, error } = await supabase
      .from('geografia_estados')
      .select('estado_id, nombre_estado')
      .order('nombre_estado', { ascending: true });

    if (error) {
      console.error('Error fetching estados:', error);
      throw new Error('No se pudieron cargar los estados');
    }

    return data || [];
  }

  /**
   * Obtiene las ciudades correspondientes a un estado específico.
   */
  static async getCiudadesPorEstado(estadoId: number): Promise<Ciudad[]> {
    const { data, error } = await supabase
      .from('geografia_ciudades')
      .select('ciudad_id, ciudad_nombre, latitud, longitud')
      .eq('estado_id', estadoId)
      .order('ciudad_nombre', { ascending: true });

    if (error) {
      console.error('Error fetching ciudades:', error);
      throw new Error('No se pudieron cargar las ciudades');
    }

    return data || [];
  }

  static async getTopCiudades(): Promise<Ciudad[]> {
    // Para MVP, traemos algunas ciudades conocidas quemando los nombres o con un limit.
    // Idealmente, en DB tendríamos un flag `es_principal = true`.
    const { data, error } = await supabase
      .from('geografia_ciudades')
      .select('ciudad_id, ciudad_nombre, latitud, longitud, geografia_estados(nombre_estado)')
      .in('ciudad_nombre', ['Caracas', 'Maracaibo', 'Valencia', 'Barquisimeto', 'Maracay', 'Acarigua', 'Araure']);

    if (error) {
      console.error('Error fetching top ciudades:', error);
      return [];
    }

    let ciudades: Ciudad[] = (data || []).map((row: any) => ({
      ciudad_id: row.ciudad_id,
      ciudad_nombre: row.ciudad_nombre,
      latitud: row.latitud,
      longitud: row.longitud,
      estado_nombre: row.geografia_estados?.nombre_estado,
    }));

    // Fallback: Si Araure no está en la base de datos aún, la inyectamos para el demo
    if (!ciudades.find(c => c.ciudad_nombre.toLowerCase() === 'araure')) {
      ciudades.push({
        ciudad_id: 99999,
        ciudad_nombre: 'Araure',
        latitud: 9.5606,
        longitud: -69.2136,
        estado_nombre: 'Portuguesa',
      });
    }

    // Orden alfabético
    ciudades.sort((a, b) => a.ciudad_nombre.localeCompare(b.ciudad_nombre));

    return ciudades;
  }

  /**
   * Busca ciudades de forma asíncrona predictiva por coincidencia de texto.
   */
  static async searchCiudades(query: string): Promise<Ciudad[]> {
    if (!query || query.length < 2) return [];

    const { data, error } = await supabase
      .from('geografia_ciudades')
      .select('ciudad_id, ciudad_nombre, latitud, longitud, geografia_estados(nombre_estado)')
      .ilike('ciudad_nombre', `%${query}%`)
      .limit(10);

    if (error) {
      console.error('Error searching ciudades:', error);
      return [];
    }

    return (data || []).map((row: any) => ({
      ciudad_id: row.ciudad_id,
      ciudad_nombre: row.ciudad_nombre,
      latitud: row.latitud,
      longitud: row.longitud,
      estado_nombre: row.geografia_estados?.nombre_estado,
    }));
  }
}
